import React, { useContext, useEffect, useState } from 'react';
import { Box, Button, Stack, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useMenu } from "../../../hooks/report/useMenu";
import moment from "moment";
import { AppContext } from '../../../context/app-context';
import { AdminBookingReportVO } from "../../../types/model/valueObject/booking/AdminBookingReportVO";
import { adminBookingsReportEndpoints } from "../../../requests/api-endpoints";
import { AdminBookingDetailsBody } from "./AdminBookingDetailsBody";
import { AdminBookingSummary } from "./AdminBookingSummary";
import { dialogs } from "./AdminBookingDialogWrapper";
import { ReportDialogSetterProps } from "../../../interfaces";
import { hasSuperAdminAccess } from "../../../util/AccountUtils";
import {
  BookingDurationType,
  BookingStatus,
  BookingSubscriptionStatus
} from "../../constants/securspace-constants";

import {
  ActionItem,
  ActionsMenu,
  ActionsMenuOpenButton,
  CollapsableCardBodyActionsContainer,
  CollapsableCardBodyContainer,
  CollapsableCardClickableContainer,
  CollapsableCardCollapseContainer,
  CollapsableCardContainer
} from "../../cardComponents";
import {
  ActionTypes,
  ActionDefaultIcons,
  executeDownload
} from "../../../util/ActionUtils";

type AdminBookingCardListItemProps = {
  item: AdminBookingReportVO;
  expanded?: boolean;
  onToggleExpand: () => void;
  onOpenDialog: (dialog: ReportDialogSetterProps<AdminBookingReportVO>) => void;
}

interface BookingAction {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  show?: boolean;
  enable?: boolean;
  tooltip?: string;
  destructive?: boolean;
}

const downloadActionPayload = {
  endpoint: (item: AdminBookingReportVO) =>
              adminBookingsReportEndpoints.actions.downloadBookingHistory(item.id),
  errorMessage: "Unable to download history for this booking",
};

// Already manual payment, or terminal: converting a terminal booking would reactivate it (the API rejects it too)
const NOT_ELIGIBLE_FOR_MANUAL_PAYMENT: readonly string[] = [
  BookingStatus.MANUAL_PAYMENT,
  BookingStatus.DECLINED,
  BookingStatus.CANCELLED,
  BookingStatus.EXPIRED,
];

// Matches the redesign mockup: white surface, tighter radius and layered shadow
const cardSx = {
  backgroundColor: '#fff',
  borderRadius: '0.4em',
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.06), 0 4px 10px rgba(0, 0, 0, 0.1)',
};

/**
 * Component that displays all information for a single booking
 * @param item the AdminBookingReportVO to display
 * @param expanded
 * @param onToggleExpand
 * @param onOpenDialog
 * @constructor
 */
const AdminBookingCardListItem = ({
  item,
  expanded,
  onToggleExpand,
  onOpenDialog
}: AdminBookingCardListItemProps) => {
  const { user } = useContext(AppContext);
  const { anchorEl, isOpen, handleClick, handleClose } = useMenu();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  const [showActions, setShowActions] = useState(false);

  // Collapsing the card always returns it to the details view
  useEffect(() => {
    if (!expanded) setShowActions(false);
  }, [expanded]);

  const handleAction = (dialog: string) => {
    handleClose();
    onOpenDialog({ item, dialog })
  }

  const isNotDaily = Boolean(item?.durationType) && item.durationType.toUpperCase() !== BookingDurationType.DAILY.toUpperCase();
  const chargeOveragesOn = item?.chargeOverages?.toUpperCase() === 'YES';

  const actions: BookingAction[] = [
    {
      label: 'Adjust End Date',
      icon: ActionDefaultIcons[ActionTypes.DIALOG],
      onClick: () => handleAction(dialogs.ADJUST_END_DATE),
      show: item?.durationType?.toUpperCase() !== BookingDurationType.DAILY.toUpperCase(),
    },
    {
      label: 'Cancel Booking',
      icon: ActionDefaultIcons[ActionTypes.DIALOG],
      onClick: () => handleAction(dialogs.CANCEL_BOOKING),
      show: item?.status !== BookingStatus.CANCELLED && (
        moment(item?.startDate).isAfter(moment().add(1, "days")) ||
        hasSuperAdminAccess(user)
      ),
      destructive: true,
    },
    {
      label: 'Toggle Charge Overages',
      icon: ActionDefaultIcons[ActionTypes.DIALOG],
      onClick: () => handleAction(dialogs.TOGGLE_CHARGE_OVERAGES),
      show: Boolean(item?.id),
    },
    {
      label: 'Set to Manual Payment',
      icon: ActionDefaultIcons[ActionTypes.DIALOG],
      onClick: () => handleAction(dialogs.SET_TO_MANUAL_PAYMENT),
      show: !NOT_ELIGIBLE_FOR_MANUAL_PAYMENT.includes(item?.status),
    },
    {
      label: 'End Booking',
      icon: ActionDefaultIcons[ActionTypes.DIALOG],
      onClick: () => handleAction(dialogs.END_BOOKING),
      show: isNotDaily &&
        moment(item?.endDate).isAfter(moment()) &&
        item?.bookingSubscriptionStatus?.toUpperCase() !== BookingSubscriptionStatus.CANCELLED.toUpperCase(),
      destructive: true,
    },
    {
      label: 'Create Next Invoice',
      icon: ActionDefaultIcons[ActionTypes.DIALOG],
      onClick: () => handleAction(dialogs.CREATE_NEXT_INVOICE),
      show: item?.status === BookingStatus.MANUAL_PAYMENT && isNotDaily,
    },
    {
      label: 'Create Overage Invoice',
      icon: ActionDefaultIcons[ActionTypes.DIALOG],
      onClick: () => handleAction(dialogs.CREATE_OVERAGE_INVOICE),
      show: item?.status === BookingStatus.MANUAL_PAYMENT && hasSuperAdminAccess(user),
      enable: !chargeOveragesOn,
      tooltip: chargeOveragesOn
        ? 'Turn off Charge Overages for this booking to create an overage invoice.'
        : undefined,
    },
    {
      label: 'Download Booking History',
      icon: ActionDefaultIcons[ActionTypes.DOWNLOAD],
      onClick: () => executeDownload(item, downloadActionPayload, handleClose),
      enable: Boolean(item?.id),
    },
  ];

  // Destructive actions are listed last on mobile
  const mobileActions = [
    ...actions.filter(action => !action.destructive),
    ...actions.filter(action => action.destructive),
  ].filter(action => action.show !== false);

  return (
    <CollapsableCardContainer expanded={expanded} variant="flush" sx={cardSx}>
        <Box sx={{ width: '100%', borderBottom: expanded ? '1px solid #eee' : 'none' }}>
          <CollapsableCardClickableContainer
            onClick={onToggleExpand}
            {...(isMobile && {
              role: 'button',
              tabIndex: 0,
              'aria-expanded': expanded ?? false,
              onKeyDown: (e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onToggleExpand();
                }
              },
            })}
          >
            <AdminBookingSummary item={item} expanded={expanded ?? false} />
          </CollapsableCardClickableContainer>
        </Box>
        <CollapsableCardCollapseContainer in={expanded}>
          <Box sx={{ px: 2, pb: 2 }}>
            <CollapsableCardBodyContainer>
              {isMobile ? (
                <Box sx={{ width: '100%', minWidth: 0 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    startIcon={showActions ? <ArrowBackIcon /> : <MoreHorizIcon />}
                    onClick={() => setShowActions(prev => !prev)}
                    sx={{ mb: 2, fontWeight: 700 }}
                  >
                    {showActions ? 'View Details' : 'Actions'}
                  </Button>
                  {showActions ? (
                    <Stack component="ul" aria-label="Booking actions" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                      {mobileActions.map(action => {
                        const button = (
                          <Button
                            fullWidth
                            color={action.destructive ? 'error' : 'inherit'}
                            disabled={action.enable === false}
                            endIcon={<ChevronRightIcon />}
                            onClick={action.onClick}
                            sx={{
                              justifyContent: 'space-between',
                              textTransform: 'none',
                              fontWeight: 500,
                              py: 1.5,
                              px: 0.5,
                              borderRadius: 0,
                              borderBottom: '1px solid #eee',
                              textAlign: 'left',
                            }}
                          >
                            {action.label}
                          </Button>
                        );
                        return (
                          <li key={action.label}>
                            {action.tooltip ? (
                              <Tooltip title={action.tooltip} describeChild enterTouchDelay={0}>
                                <Box component="span" tabIndex={action.enable === false ? 0 : undefined} sx={{ display: 'block' }}>{button}</Box>
                              </Tooltip>
                            ) : button}
                          </li>
                        );
                      })}
                    </Stack>
                  ) : (
                    <AdminBookingDetailsBody item={item} />
                  )}
                </Box>
              ) : (
                <>
                  <AdminBookingDetailsBody item={item} />
                  <CollapsableCardBodyActionsContainer>
                    <ActionsMenuOpenButton onClick={handleClick} />
                    <ActionsMenu open={isOpen} anchorEl={anchorEl} onClose={handleClose}>
                      {actions.map(action => (
                        <ActionItem
                          key={action.label}
                          label={action.label}
                          icon={action.icon}
                          onClick={action.onClick}
                          show={action.show}
                          enable={action.enable}
                          tooltip={action.tooltip}
                        />
                      ))}
                    </ActionsMenu>
                  </CollapsableCardBodyActionsContainer>
                </>
              )}
            </CollapsableCardBodyContainer>
          </Box>
        </CollapsableCardCollapseContainer>
      </CollapsableCardContainer>
  );
};

export { AdminBookingCardListItem };
